import { supabase } from '../lib/supabase';
import { isDemoMode } from '../lib/demo';
import { demoStore } from '../demo/store';
import { base64ToArrayBuffer } from '../lib/images';
import { contactFriendRepo } from './contactFriendRepo';
import { phoneKey } from '../lib/contacts';
import { sha256Hex } from '../lib/sha256';
import { reportError } from '../lib/report';
import { signOutEverywhere } from '../lib/authSignOut';
import { revokeAppleAccess } from '../lib/appleRevoke';
import { USERNAME_MIN, normalizeUsername } from '../lib/username';
import { TERMS_VERSION } from '../legal/terms';
import { assertCleanForMe } from './reportRepo';
import type { Friend, Group, MemberRef, Profile, UserId } from '../domain/types';

/* eslint-disable @typescript-eslint/no-explicit-any */
const AVATAR_BUCKET = 'avatars';
/** عکسِ مخاطبینِ خودِ کاربر؛ هر دو باکت با پوشه‌ی `<uid>/` تفکیک شده‌ن. */
const CONTACT_AVATAR_BUCKET = 'contact-avatars';

/**
 * همه‌ی فایل‌های پوشه‌ی یه کاربر توی یه باکت رو پاک می‌کنه.
 * خطا رو گزارش می‌ده ولی throw نمی‌کنه: نموندنِ یه عکس نباید جلوی حذفِ حساب
 * رو بگیره — برعکسش (حسابِ پاک‌شده و عکسِ باقی‌مانده) بدتره.
 */
async function wipeFolder(bucket: string, uid: UserId): Promise<void> {
  const { data, error } = await supabase.storage.from(bucket).list(uid, { limit: 1000 });
  if (error) { reportError('profileRepo.wipeFolder.list', error, { bucket }); return; }
  const paths = (data ?? []).map((f) => `${uid}/${f.name}`);
  if (!paths.length) return;
  const { error: e2 } = await supabase.storage.from(bucket).remove(paths);
  if (e2) reportError('profileRepo.wipeFolder.remove', e2, { bucket, count: paths.length });
}

/**
 * باکتِ `avatars` عمومیه، پس آدرسش بدون رفت‌وبرگشت به سرور ساخته میشه.
 * (برخلاف `contact-avatars` که private است و signed URL می‌خواد — عکس مخاطب
 * داده‌ی یه آدم سومه، ولی عکس پروفایل رو خودِ کاربر برای دیده شدن گذاشته.)
 */
export const avatarUrlOf = (path?: string | null): string | null => (path
  ? supabase.storage.from(AVATAR_BUCKET).getPublicUrl(path).data.publicUrl
  : null);

export const mapProfile = (p: any): Profile => ({
  id: p.id, username: p.username, displayName: p.display_name,
  avatarEmoji: p.avatar_emoji, city: p.city, country: p.country,
  // bigint از PostgREST رشته می‌آد — نگاه کن به mapEvent توی miscRepo.
  cityId: p.city_id == null ? null : Number(p.city_id), statusText: p.status_text,
  avatarPath: p.avatar_path ?? null, avatarUrl: avatarUrlOf(p.avatar_path),
  // فقط سرور می‌خواندش (تریگرهای اعلانِ ۰۱۰۹)؛ این‌جا هست تا syncLocale
  // بفهمد لازم است دوباره بنویسد یا نه.
  locale: p.locale ?? null,
  // گیتِ پذیرشِ مجدد از روی همین می‌خواند — نگاه کن به needsTermsAcceptance.
  termsVersion: p.terms_version ?? null,
  isAdmin: p.is_admin ?? false,
});

export const profileRepo = {
  async me(): Promise<Profile | null> {
    if (isDemoMode) return demoStore.profile.me();
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) return null;
    const { data } = await supabase.from('profiles').select('*').eq('id', auth.user.id).maybeSingle();
    if (!data) return null;
    /**
     * حسابِ حذف‌شده هیچ راهِ برگشتی ندارد.
     *
     * توکنِ قبلی تا انقضاش معتبر می‌مونه، پس بدون این شرط کسی که موقعِ حذف
     * اینترنتش قطع شده بود می‌تونست با همون نشست وارد بمونه و پوسته‌ی
     * ناشناس‌شده رو ببینه. حذفی که با یه لاگین برگرده، حذف نیست.
     */
    /*
     * و خروجِ این‌جا باید حتماً بگیرد: نشستِ سمتِ سرور را همان تابعِ حذف کشته،
     * پس درخواستِ خروج فقط ۴۰۳ می‌گیرد. `accountGone` یعنی اول توکن را از
     * دستگاه برمی‌داریم و اصلاً نمی‌پرسیم — نگاه کن به lib/authSignOut.
     */
    if (data.deleted_at) { await signOutEverywhere('accountGone'); return null; }
    return mapProfile(data);
  },

  /**
   * ساختِ نمایه — و تنها جایی که پذیرشِ شرایط ثبت می‌شود.
   *
   * `acceptedTerms` عمداً پارامترِ **اجباری** است و نه یک فیلدِ اختیاری در
   * `extra`. دلیلش یک اشکالِ واقعی بود:
   *
   * تیکِ پذیرش روی فرمِ ثبت‌نام می‌نشست و فقط وقتی شرط می‌شد که حالت روی
   * «ساخت حساب» بود. ولی دکمه‌ی «ادامه با گوگل» در حالتِ «ورود» هم برای
   * کسی که حساب ندارد حساب **می‌سازد** — پس هر کسی می‌توانست بی‌هیچ تیکی
   * وارد شود، و همین‌جا `terms_version` برایش نوشته می‌شد. یعنی دیتابیس
   * می‌گفت پذیرفته، بی‌آنکه پذیرفته باشد؛ و بند ۲۱ سند دقیقاً همین ردیف را
   * سندِ پذیرش می‌داند. رکوردِ دروغین از نبودنِ رکورد بدتر است.
   *
   * حالا تیک روی قدمِ ساختِ نمایه است — همان یک قدمی که هر حساب، با هر
   * ارائه‌دهنده‌ای و روی هر دو سطح، از آن رد می‌شود — و اجباری بودنِ این
   * پارامتر یعنی TypeScript نمی‌گذارد فراخوانیِ تازه‌ای بدونش کامپایل شود.
   */
  async createMine(
    username: string, displayName: string,
    extra: { acceptedTerms: boolean; city?: string; country?: string; cityId?: number | null },
  ): Promise<Profile> {
    // نگهبانِ زمانِ اجرا کنارِ نگهبانِ زمانِ کامپایل: `acceptedTerms` ممکن است
    // از یک state بیاید، و آن state ممکن است `false` باشد.
    if (!extra.acceptedTerms) throw new Error('terms not accepted');
    /**
     * نامِ کاربری اجباری است و این‌جا دیگر ساخته نمی‌شود.
     *
     * تا دیروز همین خط اگر چیزی نمی‌گرفت `user` + پنج رقم از ساعتِ سیستم
     * می‌ساخت — که برای هر اسمِ غیرلاتین یعنی همیشه، و چون این فیلد بعداً
     * عوض نمی‌شود، تا ابد. حالا پیشنهاد سرِ فرم ساخته و **به کاربر نشان**
     * داده می‌شود (`lib/username.ts` و `useUsernameField`)، و این‌جا فقط
     * نگهبانِ زمانِ اجراست: رسیدنِ رشته‌ی خالی به این‌جا یعنی فرم خراب است،
     * و ساکت ماندن و یک اسمِ بی‌ربط ساختن بدتر از خطاست.
     */
    const uname = normalizeUsername(username);
    if (uname.length < USERNAME_MIN) throw new Error('username required');
    // فیلترِ واژه‌های ناپسند (WHOSUP-309): پیش از دمو هم، تا رفتار یکی باشد.
    await assertCleanForMe('profile_name', displayName, uname);
    if (isDemoMode) return demoStore.profile.createMine(uname, displayName, extra);
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) throw new Error('not signed in');
    const { data, error } = await supabase
      .from('profiles')
      .insert({
        id: auth.user.id,
        username: uname,
        display_name: displayName,
        // بند ۲۱ سند: باید بشود گفت کاربر کدام نسخه را کِی پذیرفته. تیک
        // همین صفحه است و همین‌جا هم ثبت می‌شود — پیش از ساخته شدن نمایه
        // ردیفی وجود ندارد که روی آن بنویسیم.
        terms_version: TERMS_VERSION,
        terms_accepted_at: new Date().toISOString(),
        ...(extra.city ? { city: extra.city.trim() } : {}),
        ...(extra.country ? { country: extra.country.trim() } : {}),
        // شناسه کنارِ اسم می‌شینه، نه به‌جاش — نگاه کن به Profile.cityId.
        ...(extra.cityId ? { city_id: extra.cityId } : {}),
      })
      .select('*').single();
    if (error) throw error;
    return mapProfile(data);
  },

  /**
   * پذیرشِ نسخه‌ی جاریِ سند، برای حسابی که از قبل وجود دارد.
   *
   * قرینه‌ی همان دو ستونی که `createMine` بالا می‌نویسد — همان‌جا برای
   * کاربرِ تازه، این‌جا برای کاربری که نسخه‌ی قبلی را پذیرفته بوده و حالا
   * متن عوض شده (بند ۲۱). تاریخ هم دوباره نوشته می‌شود: چیزی که ثبت
   * می‌کنیم «کِی این نسخه را پذیرفت» است، نه «کِی اولین بار عضو شد».
   */
  async acceptCurrentTerms(): Promise<Profile | null> {
    if (isDemoMode) return demoStore.profile.acceptTerms();
    const { data: auth } = await supabase.auth.getUser();
    if (!auth.user) throw new Error('not signed in');
    const { data, error } = await supabase
      .from('profiles')
      .update({
        terms_version: TERMS_VERSION,
        terms_accepted_at: new Date().toISOString(),
      })
      .eq('id', auth.user.id)
      .select('*').single();
    if (error) throw error;
    return mapProfile(data);
  },

  /**
   * آزاد است؟ — برای فرمِ ثبت‌نام، پیش از ساختِ حساب.
   *
   * قیدِ یکتاییِ ستون همچنان حرفِ آخر را می‌زند و این جای آن را نمی‌گیرد:
   * سیاستِ `p_sel` (مایگریشن 0046) نمایه‌های حذف‌شده را پنهان می‌کند، پس
   * نامِ کاربریِ یک حسابِ حذف‌شده از این‌جا «آزاد» دیده می‌شود ولی درج
   * سرِ همان قید می‌شکند. برای همین فرم‌ها خطای درج را هم به «گرفته شده»
   * ترجمه می‌کنند. این تابع فقط کاری می‌کند که آن حالت **استثنا** باشد،
   * نه مسیرِ عادیِ هر کسی که اسمش با یکی دیگر یکی است.
   */
  async isUsernameFree(username: string): Promise<boolean> {
    const u = normalizeUsername(username);
    if (u.length < USERNAME_MIN) return false;
    if (isDemoMode) return demoStore.profile.byUsername(u) === null;
    const { data, error } = await supabase
      .from('profiles').select('id').eq('username', u).maybeSingle();
    if (error) throw error;
    return !data;
  },

  async byUsername(username: string): Promise<Profile | null> {
    if (isDemoMode) return demoStore.profile.byUsername(username);
    const { data } = await supabase
      .from('profiles').select('*').eq('username', username.toLowerCase()).maybeSingle();
    return data ? mapProfile(data) : null;
  },

  /**
   * اسم، شهر، عکس و متنِ وضعیت. فقط فیلدهایی که دادی نوشته می‌شن.
   *
   * `statusText` هیچ فرمی ندارد که پرش کند (ستونش از مایگریشن 0001 مانده)،
   * ولی حساب‌های قدیمی مقدار دارند و باید بشود پاکش کرد — نگاه کن به
   * `dataEraseRepo`.
   */
  async updateMine(uid: UserId, patch: {
    displayName?: string; city?: string; country?: string;
    cityId?: number | null; avatarPath?: string | null; statusText?: string | null;
  }): Promise<void> {
    // فیلترِ واژه‌های ناپسند (WHOSUP-309): پیش از دمو هم، تا رفتار یکی باشد.
    await assertCleanForMe('profile_name', patch.displayName, patch.statusText);
    if (isDemoMode) return demoStore.profile.updateMine(patch);
    const row: Record<string, unknown> = {};
    if (patch.displayName !== undefined) row.display_name = patch.displayName;
    if (patch.city !== undefined) row.city = patch.city;
    if (patch.country !== undefined) row.country = patch.country;
    if (patch.cityId !== undefined) row.city_id = patch.cityId;
    if (patch.avatarPath !== undefined) row.avatar_path = patch.avatarPath;
    if (patch.statusText !== undefined) row.status_text = patch.statusText;
    if (Object.keys(row).length === 0) return;

    const { error } = await supabase.from('profiles').update(row).eq('id', uid);
    if (error) throw error;
  },

  /** عکس رو توی پوشه‌ی خودِ کاربر می‌ذاره و مسیرش رو برمی‌گردونه. */
  async uploadAvatar(uid: UserId, base64: string): Promise<string> {
    if (isDemoMode) return demoStore.profile.uploadAvatar(base64);
    // اسم یکتا: آدرس عمومی کش میشه، پس اگه اسم ثابت بود عکس قدیمی می‌موند.
    const path = `${uid}/${Date.now()}-${Math.random().toString(36).slice(2, 8)}.jpg`;
    const body = base64ToArrayBuffer(base64);
    const { error } = await supabase.storage
      .from(AVATAR_BUCKET)
      .upload(path, body, { contentType: 'image/jpeg', upsert: false });
    if (error) {
      // اندازه رو هم لاگ کن: بیشترِ شکست‌های آپلود سرِ سقفِ حجمِ باکت اتفاق می‌افته.
      reportError('profileRepo.uploadAvatar', error, { bytes: body.byteLength, path });
      throw error;
    }
    return path;
  },

  /**
   * عکسِ حسابِ گوگل را یک‌بار، سرِ ساختِ نمایه، به باکتِ خودمان می‌آورد
   * (WHOSUP-307). آدرسِ گوگل را مستقیم نگه نمی‌داریم: نمایه فقط مسیرِ باکت
   * را می‌شناسد، و آدرسِ بیرونی هر وقت گوگل بخواهد می‌میرد. شکستش هرگز
   * مانعِ ساختِ حساب نیست — فقط بی‌عکس می‌ماند.
   */
  async importAvatarFromUrl(uid: UserId, url: string): Promise<void> {
    if (isDemoMode || !url) return;
    try {
      const res = await fetch(url);
      if (!res.ok) throw new Error(`avatar fetch ${res.status}`);
      const body = await res.arrayBuffer();
      const path = `${uid}/${Date.now()}-provider.jpg`;
      const { error } = await supabase.storage
        .from(AVATAR_BUCKET)
        .upload(path, body, { contentType: res.headers.get('content-type') || 'image/jpeg', upsert: false });
      if (error) throw error;
      await this.updateMine(uid, { avatarPath: path });
    } catch (e) {
      reportError('profileRepo.importAvatarFromUrl', e);
    }
  },

  /** عکس قبلی بعد از جایگزینی پاک میشه تا باکت زباله جمع نکنه. خطاش مهم نیست. */
  async removeAvatar(path?: string | null): Promise<void> {
    if (isDemoMode) return demoStore.profile.removeAvatar();
    if (!path) return;
    const { error } = await supabase.storage.from(AVATAR_BUCKET).remove([path]);
    if (error) reportError('profileRepo.removeAvatar', error, { path });
  },

  /**
   * حذفِ حساب. برمی‌گردونه چه اتفاقی افتاد:
   *   `anonymized` - نام، نام کاربری، ایمیل و شماره هش شدن و بقیه‌ی داده پاک شد
   *   `purged`     - هیچ ردیفی نموند (وقتی دلیل دقیقاً `delete` باشه؛ راهِ ادمین)
   *
   * منطقش سمتِ دیتابیسه (مایگریشن 0046) نه این‌جا: پاک کردنِ بیست جدول با
   * بیست درخواستِ جداگانه یعنی هر قطعیِ شبکه یه حسابِ نصفه‌حذف‌شده می‌سازه.
   * تابعِ SQL یه تراکنشه — یا همه‌ش، یا هیچ‌کدوم.
   *
   * فایل‌های ذخیره‌سازی استثنان: پاک کردنشون از SQL فقط ردیفِ فهرست رو می‌بره
   * و خودِ فایل روی سرور می‌مونه. پس اول این‌جا و با API واقعی پاک می‌شن.
   *
   * و قبل از همه، دسترسیِ «ورود با Apple» پس گرفته می‌شه (WHOSUP-251،
   * `lib/appleRevoke`). اول، چون `delete_my_account` نشست رو می‌کشه و بعدش
   * تابعِ `revoke-apple` دیگه صدازننده رو نمی‌شناسه. شکستش حذف رو متوقف
   * نمی‌کنه — فقط گزارش می‌شه.
   */
  async deleteMine(uid: UserId, reason: string): Promise<'anonymized' | 'purged'> {
    if (isDemoMode) { demoStore.reset(); return 'anonymized'; }
    await revokeAppleAccess();
    await Promise.all([
      wipeFolder(AVATAR_BUCKET, uid),
      wipeFolder(CONTACT_AVATAR_BUCKET, uid),
    ]);
    const { data, error } = await supabase.rpc('delete_my_account', { p_reason: reason });
    if (error) throw error;
    return data === 'purged' ? 'purged' : 'anonymized';
  },
};

/**
 * نتیجه‌ی یک درخواستِ دوستی — هر حالتی که کاربر باید ازش خبردار شه.
 *
 * `already` جدا از `sent` است چون پیامش فرق می‌کنه: «فرستاده شد» برای کسی
 * که از قبل دوستته یا درخواستت پیشش منتظره، دروغه.
 */
export type RequestResult = 'sent' | 'not_found' | 'invalid' | 'self' | 'already';

const REQUEST_RESULTS: readonly RequestResult[] = ['sent', 'not_found', 'invalid', 'self', 'already'];

/**
 * خودِ درخواست، مشترکِ هر دو مسیرِ (نامِ کاربری / شماره و ایمیل).
 *
 * درجِ مستقیم نیست، `request_friendship` است (0152 برای WHOSUP-261، و 0153
 * برای WHOSUP-262 که همان را تنها درِ همه‌ی مسیرها کرد): `friendships_pair`
 * برای هر جفت آدم یک ردیف جا دارد، و بعد از «حذفِ دوست»، «رد» یا رفعِ
 * مسدودیت آن ردیف روی `removed`/`declined` مانده بود — درجِ تازه به قیدِ
 * یکتایی می‌خورد و این‌جا «از قبل دوستید» نشان داده می‌شد. تابع همان ردیف
 * را دوباره «در انتظار» می‌کند و اعلانش را هم می‌فرستد.
 *
 * ⚠ منبع (`source`) پارامتر نیست: از اپ همیشه `link` است و مسیرهای خودکار
 * از تابعِ داخلیِ `request_friendship_as` می‌روند که منبعِ خودشان را می‌دهند.
 */
async function sendRequest(myId: UserId, targetId: UserId): Promise<RequestResult> {
  if (targetId === myId) return 'self';
  const { data, error } = await supabase.rpc('request_friendship', { p_target: targetId });
  if (error) throw error;
  const r = String(data) as RequestResult;
  return REQUEST_RESULTS.includes(r) ? r : 'sent';
}

export const friendRepo = {
  async list(uid: UserId): Promise<Friend[]> {
    if (isDemoMode) return demoStore.friends.list();
    const { data, error } = await supabase
      .from('friendships')
      .select('*, requester:profiles!friendships_requester_id_fkey(*), addressee:profiles!friendships_addressee_id_fkey(*)')
      .in('status', ['pending', 'accepted']);
    if (error) throw error;
    return (data ?? []).map((f: any): Friend => {
      const other = f.requester_id === uid ? f.addressee : f.requester;
      const status: Friend['status'] =
        f.status === 'accepted' ? 'accepted'
          : f.requester_id === uid ? 'pending_out' : 'pending_in';
      return { friendshipId: f.id, profile: mapProfile(other), status };
    });
  },

  /**
   * چند نفر توی فهرستِ دوستانم‌اند — بدونِ آوردنِ خودِ ردیف‌ها.
   *
   * `head: true` یعنی سرور فقط می‌شماره و هیچ ردیفی برنمی‌گردونه. تنها
   * مصرفش تصمیمِ «هیچ‌کس رو نداره» است (نگاه کن به lib/noFriendsNotice)، که
   * فقط به صفر یا غیرِ صفر نیاز داره — نه به اسم و عکسِ صدها نفر.
   *
   * همون شرطِ `list` رو داره: تاییدشده و در انتظار، هر دو طرف. شناسه‌ی خودم
   * لازم نیست — محدود شدن به ردیف‌های خودم کارِ سیاستِ دیتابیسه، مثلِ `list`
   * که اون هم فقط برای تشخیصِ «طرفِ مقابل کیه» uid می‌گیره.
   */
  async count(): Promise<number> {
    if (isDemoMode) return demoStore.friends.list().length;
    const { count, error } = await supabase
      .from('friendships')
      .select('id', { count: 'exact', head: true })
      .in('status', ['pending', 'accepted']);
    if (error) throw error;
    return count ?? 0;
  },

  async request(myId: UserId, username: string): Promise<RequestResult> {
    if (isDemoMode) return demoStore.friends.request(username);
    const target = await profileRepo.byUsername(username);
    if (!target) return 'not_found';
    return sendRequest(myId, target.id);
  },

  /**
   * درخواستِ دوستی با شماره یا ایمیل، به‌جای نامِ کاربری.
   *
   * ═══ چرا هش، و چرا این تابعِ خاص ═══
   *
   * جدولِ `user_contact_keys` مالِ کاربرهای دیگه‌ست و سیاستش اجازه‌ی خوندنش
   * رو نمی‌ده — که درسته: وگرنه هر کسی می‌تونست دفترچه‌ی کلِ اپ رو در بیاره.
   * تنها درِ ورودیِ مجاز `match_contacts` است (مایگریشن 0021): هشِ sha256 رو
   * می‌گیره و فقط می‌گه «این هش به این حساب می‌خوره»، بی‌اینکه خودِ کلید
   * هیچ‌وقت برگرده.
   *
   * پس عیناً همون کاری می‌کنیم که تطبیقِ مخاطبینِ موبایل می‌کنه، فقط با یک
   * ورودی به‌جای پونصد تا.
   *
   * ⚠️ نرمال‌سازی باید **دقیقاً** قرینه‌ی چیزی باشه که موقعِ ثبت ذخیره شده،
   * وگرنه هش هیچ‌وقت نمی‌خوره و همه‌چی بی‌صدا «پیدا نشد» می‌ده:
   *   • شماره → `phoneKey` (ده رقمِ آخر) — همون چیزی که phoneRepo ذخیره می‌کنه
   *   • ایمیل → با حروفِ کوچک — چون خودِ تابع `lower(k.value)` رو هش می‌کنه
   *
   * ⚠️ عمداً «جستجو» و «فرستادنِ درخواست» یک عمل‌اند، نه دو تا: `match_contacts`
   * یه کشف ثبت می‌کنه و به طرفِ مقابل خبر می‌ده. اگه جستجوی جدا داشتیم، کسی
   * که فقط شماره‌ای رو امتحان کرده بود هم اعلان می‌فرستاد — بی‌اینکه اصلاً
   * درخواستی در کار باشه.
   */
  async requestByContact(
    myId: UserId, kind: 'phone' | 'email', raw: string,
  ): Promise<RequestResult> {
    const value = kind === 'phone' ? phoneKey(raw) : raw.trim().toLowerCase();
    if (!value || (kind === 'email' && !value.includes('@'))) return 'invalid';
    if (isDemoMode) return demoStore.friends.requestByContact(value);

    const { data, error } = await supabase.rpc('match_contacts', {
      p_hashes: [sha256Hex(value)],
    });
    if (error) throw error;
    const hit = (data ?? [])[0] as any;
    if (!hit?.user_id) return 'not_found';
    if (hit.user_id === myId) return 'self';
    return sendRequest(myId, String(hit.user_id));
  },

  /**
   * «یه آدم اضافه کن» — یک فرم، یک تصمیم (WHOSUP-148).
   *
   * خواسته‌ی صریح: کاربر هر چه از طرف دارد (اسم، شماره، ایمیل، نامِ کاربری)
   * یک‌جا می‌دهد و خودِ اپ تصمیم می‌گیرد، نه کاربر بین چند حالت:
   *
   *   ۱. نامِ کاربری داده و وجود دارد → درخواستِ دوستیِ دوطرفه.
   *   ۲. وگرنه شماره/ایمیل به یک عضو می‌خورد (همان هشِ `match_contacts`
   *      که requestByContact بالا شرحش داده) → درخواستِ دوستی.
   *   ۳. هیچ‌کس پیدا نشد → دوستِ غیرِاپیِ یک‌طرفه با همین اسم/شماره/ایمیل
   *      ساخته می‌شود (contactFriendRepo.create).
   *
   * وقتی طرف عضو است، `displayName` برمی‌گردد تا UI **اسمی که خودِ او
   * انتخاب کرده** را نشان بدهد و بگوید چه شد — نه اسمی که فرستنده تایپ
   * کرده. `userId`/`contactId` هم برمی‌گردد تا فرم بتواند عضویتِ گروه‌های
   * انتخاب‌شده را ثبت کند (سیاستِ gm_ins مالکِ گروه را برای هر دو نوع عضو
   * قبول می‌کند — مایگریشن 0005).
   */
  async addPerson(myId: UserId, input: {
    name: string; phone: string; email: string; username: string;
  }): Promise<
    | { kind: 'requested' | 'already'; displayName: string; userId: UserId }
    | { kind: 'self' }
    | { kind: 'created'; contactId: number }
  > {
    const username = input.username.trim().replace(/^@/, '');

    // دموی وب: نام‌کاربری از همان فهرستِ دمو، شماره/ایمیل همیشه «پیدا نشد»
    // (دموی requestByContact بالا می‌گوید چرا)، و ته‌ش مخاطبِ دستیِ دمو.
    if (isDemoMode) {
      if (username) {
        const target = demoStore.profile.byUsername(username);
        if (target) {
          if (target.id === myId) return { kind: 'self' };
          const r = demoStore.friends.request(username);
          return {
            // «not_found» با پروفایلِ پیداشده یعنی از قبل دوستِ دموست —
            // request دمو فقط توی DISCOVERABLE می‌گردد.
            kind: r === 'sent' ? 'requested' : 'already',
            displayName: target.displayName, userId: target.id,
          };
        }
      }
      const contactId = await contactFriendRepo.create({
        name: input.name, phones: [input.phone], emails: [input.email],
      });
      return { kind: 'created', contactId };
    }

    if (username) {
      const target = await profileRepo.byUsername(username);
      if (target) {
        if (target.id === myId) return { kind: 'self' };
        const r = await sendRequest(myId, target.id);
        return {
          kind: r === 'already' ? 'already' : 'requested',
          displayName: target.displayName, userId: target.id,
        };
      }
    }

    // شماره و ایمیل با هم در یک رفت‌وبرگشت؛ اولین برخورد کافی است.
    const phoneVal = phoneKey(input.phone);
    const emailVal = input.email.trim().toLowerCase();
    const hashes = [
      ...(phoneVal ? [sha256Hex(phoneVal)] : []),
      ...(emailVal.includes('@') ? [sha256Hex(emailVal)] : []),
    ];
    if (hashes.length) {
      const { data, error } = await supabase.rpc('match_contacts', { p_hashes: hashes });
      if (error) throw error;
      const hit = (data ?? []).find((r: any) => r.user_id) as any;
      if (hit?.user_id) {
        if (hit.user_id === myId) return { kind: 'self' };
        const r = await sendRequest(myId, String(hit.user_id));
        return {
          kind: r === 'already' ? 'already' : 'requested',
          displayName: String(hit.display_name ?? ''), userId: String(hit.user_id) as UserId,
        };
      }
    }

    const contactId = await contactFriendRepo.create({
      name: input.name, phones: [input.phone], emails: [input.email],
    });
    return { kind: 'created', contactId };
  },

  async accept(friendshipId: number) {
    if (isDemoMode) return demoStore.friends.accept(friendshipId);
    const { error } = await supabase.from('friendships')
      .update({ status: 'accepted', accepted_at: new Date().toISOString() })
      .eq('id', friendshipId);
    if (error) throw error;
  },

  async decline(friendshipId: number) {
    if (isDemoMode) return demoStore.friends.decline(friendshipId);
    await supabase.from('friendships').update({ status: 'declined' }).eq('id', friendshipId);
  },

  /**
   * دوستیِ دوطرفه رو برمی‌داره. ردیف پاک نمیشه، `removed` میشه — و از
   * مایگریشن 0153 همون ردیف با یه درخواستِ تازه (از هر طرف) دوباره باز میشه.
   *
   * از `remove_friend` رد میشه و نه یه آپدیتِ مستقیم، چون «حذف» دو کاره:
   * وضعیت، و بیرون رفتن از گروه‌های هر دو طرف — همون چیزی که متنِ
   * `removeFriendHint` قول می‌ده. یه تراکنش، تا نصفه نمونه. بی‌صداست و
   * `friendship_prefs` رو دست نمی‌زنه.
   */
  async remove(friendshipId: number) {
    if (isDemoMode) return demoStore.friends.remove(friendshipId);
    const { error } = await supabase.rpc('remove_friend', { p_friendship_id: friendshipId });
    if (error) throw error;
  },
};

/** MemberRef → ستون‌های جدول. عضو گروه یا کاربر اپه یا مخاطبِ خودم (مایگریشن 0005). */
const memberColumns = (ref: MemberRef) => (ref.kind === 'user'
  ? { user_id: ref.userId, contact_id: null }
  : { user_id: null, contact_id: ref.contactId });

export const groupRepo = {
  async list(myId: UserId): Promise<Group[]> {
    if (isDemoMode) return demoStore.groups.list();
    /**
     * فقط گروه‌هایی که خودم ساختم.
     *
     * سیاستِ خواندنِ دیتابیس گروه‌هایی رو هم که فقط عضوشونم برمی‌گردونه،
     * و همون‌ها توی صفحه‌ی دوستان ظاهر می‌شدن — در حالی که دیتابیس درست
     * اجازه‌ی عوض کردنِ اعضاشون رو نمی‌ده. نتیجه: گروهی می‌دیدی که هر
     * لمسش خطا می‌داد.
     *
     * گروه توی این اپ یه فهرستِ شخصیه، نه یه فضای مشترک. پس فهرستِ کسِ
     * دیگه اصلاً جای نشون دادن نداره.
     */
    const { data, error } = await supabase
      .from('groups')
      .select('*, members:group_members(user_id, contact_id)')
      .eq('owner_id', myId)
      .order('created_at');
    if (error) throw error;
    return (data ?? []).map((g: any) => ({
      id: g.id,
      name: g.name,
      emoji: g.emoji,
      categoryId: g.category_id ?? null,
      ownerId: g.owner_id,
      // خودِ مالک موقع ساخت گروه عضو ثبت میشه؛ توی لیست اعضا نشونش نمی‌دیم.
      memberUserIds: (g.members ?? [])
        .map((m: any) => m.user_id).filter((id: string | null) => id && id !== myId),
      memberContactIds: (g.members ?? [])
        .map((m: any) => m.contact_id).filter((id: number | null) => id != null),
    }));
  },

  async create(myId: UserId, name: string, emoji: string, categoryId?: number | null): Promise<number> {
    if (isDemoMode) return demoStore.groups.create(myId, name, emoji);
    const { data: g, error } = await supabase
      .from('groups').insert({ owner_id: myId, name, emoji, category_id: categoryId ?? null })
      .select('id').single();
    if (error) throw error;
    // ردیفِ خودِ مالک: is_group_member (و در نتیجه سیاست‌های RLS) به همین تکیه می‌کنه
    const { error: e2 } = await supabase.from('group_members')
      .insert({ group_id: g.id, user_id: myId, role: 'owner' });
    if (e2) throw e2;
    return g.id;
  },

  async addMember(groupId: number, ref: MemberRef): Promise<void> {
    if (isDemoMode) return demoStore.groups.addMember(groupId, ref);
    const { error } = await supabase.from('group_members')
      .insert({ group_id: groupId, role: 'member', ...memberColumns(ref) });
    if (error) throw error;
  },

  async removeMember(groupId: number, ref: MemberRef): Promise<void> {
    if (isDemoMode) return demoStore.groups.removeMember(groupId, ref);
    const q = supabase.from('group_members').delete().eq('group_id', groupId);
    const { error } = ref.kind === 'user'
      ? await q.eq('user_id', ref.userId)
      : await q.eq('contact_id', ref.contactId);
    if (error) throw error;
  },

  async rename(groupId: number, name: string, emoji: string, categoryId?: number | null): Promise<void> {
    if (isDemoMode) return demoStore.groups.rename(groupId, name, emoji);
    const { error } = await supabase.from('groups')
      .update({ name, emoji, category_id: categoryId ?? null }).eq('id', groupId);
    if (error) throw error;
  },

  async remove(groupId: number): Promise<void> {
    if (isDemoMode) return demoStore.groups.remove(groupId);
    const { error } = await supabase.from('groups').delete().eq('id', groupId);
    if (error) throw error;
  },
};
